// @flow
import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { heights } from 'Components/widgets/navigation/Navigation.constants';
import { defaultDictionnary } from 'Components/utils/media-server/dictionary';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';

import { Text } from 'Components/frameworks/text/Text';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Button } from 'Components/frameworks/button/Button';
import { Countdown } from 'Components/frameworks/countdown/Countdown';

import * as heroUtils from './heroUtils';

type HeroSize = 'small' | 'medium' | 'large' | 'highlight' | string;

export type Props = {
  className?: string,
  contentPosition?: 'left' | 'center' | 'right',
  countdown?: {
    dateTimeEnd: *,
    days: string,
    hours: string,
    minutes: string,
    seconds: string,
  },
  hasOverlap?: boolean,
  id?: string,
  isLegacyMode?: boolean,
  link?: {
    url: string,
    label: string,
  },
  size?: HeroSize,
  style?: *,
  subtitle?: string,
  theming?: {
    titleColor?: string,
    subtitleColor?: string,
    countdownColor?: string,
    linkColor?: string,
    linkBackgroundColor?: string,
  },
  title?: string,
  trackingData?: {|
    actionGA: string,
  |},
  url: string,
};

const sizeChart = {
  small: {
    mobile: { height: '220px', paddingBottom: '60px' },
    desktop: { height: '340px', paddingBottom: '80px' },
  },
  medium: {
    mobile: { height: '320px', paddingBottom: '60px' },
    desktop: { height: '440px', paddingBottom: '100px' },
  },
  large: {
    mobile: { height: '420px', paddingBottom: '60px' },
    desktop: { height: '540px', paddingBottom: '115px' },
  },
  highlight: {
    mobile: { height: '480px', paddingBottom: '40px' },
    desktop: { height: '480px', paddingBottom: '70px' },
  },
  full: {
    mobile: { height: '100%', paddingBottom: '60px' },
    desktop: { height: '100%', paddingBottom: '115px' },
  },
};

const alignmentChart = {
  left: 'items-start rtl:text-left',
  center: 'items-center text-center',
  right: 'items-end rtl:text-right',
};

function generateBackgroundStylesAndClassName(url) {
  if (!url) {
    return {
      style: '',
      className: '',
    };
  }
  const componentId = generateUniqueId([url]);

  const widths = defaultDictionnary.getWidthRanges();

  const params = { id: componentId, url: url };
  return {
    style: (
      <style
        dangerouslySetInnerHTML={{
          __html: `${heroUtils.getAllBackgroundRules({
            ...params,
            width1x: widths[0],
            width2x: widths[1],
          })(heroUtils.getBackgroundRule)}
      @media all and (min-width: 22.51em) {
        ${heroUtils.getAllBackgroundRules({
          ...params,
          width1x: widths[2],
          width2x: widths[3],
        })(heroUtils.getBackgroundRule)}
      }
      @media all and (min-width: 40.01em) {
        ${heroUtils.getAllBackgroundRules({
          ...params,
          width1x: widths[2],
          width2x: widths[3],
        })(heroUtils.getBackgroundRule)}
      }
      @media all and (min-width: 64.01em) {
        ${heroUtils.getAllBackgroundRules({
          ...params,
          width1x: widths[3],
          width2x: widths[4],
        })(heroUtils.getBackgroundRule)}
      }
      @media all and (min-width: 76.26em) {
        ${heroUtils.getAllBackgroundRules({
          ...params,
          width1x: widths[4],
          width2x: widths[4],
        })(heroUtils.getBackgroundRule)}
      }`,
        }}
      />
    ),
    className: componentId,
  };
}

export function getHeroDimensions(sizeProp: HeroSize, isDesktop: boolean) {
  const size = sizeChart[sizeProp] ? sizeChart[sizeProp] : sizeChart.large;
  const isHighlight = sizeProp === 'highlight';
  const heroPaddingTop = isDesktop
    ? heights.NAVIGATION_LARGE_HEIGHT
    : heights.NAVIGATION_NARROW_HEIGHT;
  const highlightPaddingTop = isDesktop ? '80px' : '40px';

  return {
    height: isDesktop ? size.desktop.height : size.mobile.height,
    paddingBottom: isDesktop ? size.desktop.paddingBottom : size.mobile.paddingBottom,
    paddingTop: isHighlight ? highlightPaddingTop : heroPaddingTop,
  };
}

export function Hero(props: Props) {
  const { isDesktop } = useContext(ResponsiveContext);
  const {
    className = 'Hero',
    countdown = {
      dateTimeEnd: '',
      days: '',
      hours: '',
      minutes: '',
      seconds: '',
    },
    hasOverlap,
    link,
    id,
    isLegacyMode,
    size = 'large',
    style,
    subtitle,
    theming = {},
    title = '',
  } = props;

  const { style: styleTag, className: imageClassName } = generateBackgroundStylesAndClassName(
    props.url,
  );
  const {
    titleColor = COLORS.WHITE,
    subtitleColor = COLORS.WHITE,
    countdownColor = COLORS.WHITE,
    linkColor = COLORS.WHITE,
    linkBackgroundColor = COLORS.BLUE,
  } = theming;
  const { height, paddingBottom, paddingTop } = getHeroDimensions(size, isDesktop);
  const isHighlight = size === 'highlight';
  const textAlignment = props.contentPosition ? alignmentChart[props.contentPosition] : '';

  const TitleAndSubtitleWrapperTag = link && link.url ? TrackClick : React.Fragment;

  const titleClassName = isLegacyMode
    ? { className: 'text-4xl lg:text-7xl' }
    : { className: size !== 'small' && size !== 'highlight' ? 'lg:text-7xl' : '' };

  const titleHLevel = size !== 'highlight' ? 1 : 2;

  const trackingData = {
    actionGA: 'immersive',
    labelGA: `${title}`.trim(),
    ...props.trackingData,
  };

  const titleAndSubtitleWrapperProps =
    link && link.url
      ? {
          url: link.url,
          as: DynamicLink,
          className: 'reset-anchor hover:scale-105 transition-all inline-block',
          trackingData,
        }
      : {};

  return (
    <div
      style={{ height, marginBottom: hasOverlap ? '-80px' : 0, ...style }}
      className={`Hero--${size} ${className}`}
      id={id}
    >
      <div style={{ height, paddingTop }} className="relative">
        <div
          style={{ height }}
          className={`fadeIn absolute bg-center bg-cover bg-no-repeat h-full top-0 inset-x-0 w-full ${imageClassName}`}
        />
        {title ? (
          <div
            className="align-bottom flex flex-col h-full z-1 justify-end px-5 relative"
            style={{ paddingBottom }}
          >
            <div
              className={classnames(`mx-auto w-full max-w-site flex flex-col ${textAlignment}`, {
                'h-full': isHighlight,
              })}
            >
              <TitleAndSubtitleWrapperTag {...titleAndSubtitleWrapperProps}>
                <Text
                  text={title}
                  color={titleColor}
                  hLevel={titleHLevel}
                  theme="text-brand-1"
                  {...titleClassName}
                />
                {subtitle && !isLegacyMode ? (
                  <Text
                    text={subtitle}
                    color={subtitleColor}
                    theme="text-sans-5"
                    className="mt-1"
                  />
                ) : null}
              </TitleAndSubtitleWrapperTag>
              {!isEmpty(countdown) &&
                !isLegacyMode && (
                  <Countdown className="pt-3" {...countdown} color={countdownColor} />
                )}
              {link && link.label && !isLegacyMode ? (
                <div className={classnames({ 'mt-auto w-full': isHighlight })}>
                  <Button
                    url={link.url || '#immersive-beacon'}
                    className={classnames('mt-4', { 'w-full': !isDesktop })}
                    color={linkColor}
                    colorBg={linkBackgroundColor}
                    style={{ maxWidth: isHighlight && !isDesktop ? 'none' : '17.5rem' }}
                    trackingData={trackingData}
                  >
                    {link.label}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {styleTag}
        {title ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              /* eslint-disable no-dupe-keys */
              background: '-moz-linear-gradient(top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 100%)',
              background: '-webkit-linear-gradient(top, rgba(0,0,0,0) 0%,rgba(0,0,0,0.2) 100%)',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%,rgba(0,0,0,0.2) 100%)',
            }}
          />
        ) : null}
      </div>
      <div id="immersive-beacon" />
    </div>
  );
}
